import React from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Badge } from '@/components/ui/Badge'
import { useDepartment } from '@/hooks/useWorkforce'
import {
  Building2,
  ArrowLeft,
  Edit,
  Users,
  DollarSign,
  Briefcase,
  MapPin,
  User,
  TrendingUp,
  RefreshCw
} from 'lucide-react'

function DepartmentDetailContent() {
  const params = useParams({ from: '/_auth/workforce/departments/$departmentId/' })
  const departmentId = params.departmentId

  const { department, loading, isFetching, error, refetch } = useDepartment(departmentId)

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !department) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Departamento não encontrado'}</p>
        <Link to="/workforce/departments">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Departamentos
          </Button>
        </Link>
      </div>
    )
  }

  const budgetValue = typeof department.budget === 'number' ? department.budget : department.budget?.allocated || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/workforce/departments">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
                {department.name}
              </h1>
              <Badge className={department.status === "ACTIVE" ? 'bg-success/20 text-success' : 'bg-brand-gray/20 text-brand-gray'}>
                {department.status === "ACTIVE" ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            {department.description && (
              <p className="font-body text-brand-gray mt-2">
                {department.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <RefreshCw className="w-4 h-4 text-brand-gray animate-spin" />
          )}
          <Link to={`/workforce/departments/${departmentId}/edit` as any}>
            <Button size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-grid lg:grid-cols-4">
        <MetricCard
          title="Colaboradores"
          value={department.employeeCount || 0}
          icon={Users}
          trend="neutral"
        />
        <MetricCard
          title="Orçamento"
          value={formatCurrency(budgetValue)}
          icon={DollarSign}
          trend="neutral"
        />
        <MetricCard
          title="Cargos"
          value={department.roles?.length || 0}
          icon={Briefcase}
          trend="neutral"
        />
        <MetricCard
          title="Subdepartamentos"
          value={department.subDepartments?.length || 0}
          icon={Building2}
          trend="neutral"
        />
      </div>

      {/* Department Information */}
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">
          Informações do Departamento
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {department.costCenter && (
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Centro de Custo</div>
              <div className="font-semibold text-white">{department.costCenter}</div>
            </div>
          )}

          {department.manager && (
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Gerente</div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-brand-primary" />
                <Link to={`/workforce/employees/${department.manager.employeeId}` as any}>
                  <span className="font-semibold text-white hover:text-brand-primary transition-colors">
                    {department.manager.personalInfo.name}
                  </span>
                </Link>
              </div>
            </div>
          )}

          {department.parentDepartment && (
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Departamento Pai</div>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-brand-primary" />
                <Link to={`/workforce/departments/${department.parentDepartment.departmentId}` as any}>
                  <span className="font-semibold text-white hover:text-brand-primary transition-colors">
                    {department.parentDepartment.name}
                  </span>
                </Link>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-sm text-brand-gray">Data de Criação</div>
            <div className="font-semibold text-white">
              {new Date(department.createdAt).toLocaleDateString('pt-BR')}
            </div>
          </div>
        </div>
      </Card>

      {/* Employees in Department */}
      {department.employees && department.employees.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-semibold text-white">
              Colaboradores ({department.employees.length})
            </h2>
            <Link to={`/workforce/employees?departmentId=${departmentId}` as any}>
              <Button variant="ghost" size="sm">
                Ver Todos
              </Button>
            </Link>
          </div>

          <div className="grid gap-4">
            {department.employees.slice(0, 10).map((employee) => (
              <Link key={employee.employeeId} to={`/workforce/employees/${employee.employeeId}` as any}>
                <div className="flex items-center justify-between p-4 bg-brand-gray/5 rounded-lg hover:bg-brand-gray/10 transition-colors">
                  <div>
                    <div className="font-semibold text-white">
                      {employee.personalInfo.name}
                    </div>
                    <div className="text-sm text-brand-gray">
                      {employee.currentEngagement?.roleName || 'N/A'}
                    </div>
                  </div>
                  <Badge className={
                    employee.status === 'ACTIVE'
                      ? 'bg-success/20 text-success'
                      : 'bg-brand-gray/20 text-brand-gray'
                  }>
                    {employee.status}
                  </Badge>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Subdepartments */}
      {department.subDepartments && department.subDepartments.length > 0 && (
        <Card className="p-6">
          <h2 className="font-display text-xl font-semibold text-white mb-6">
            Subdepartamentos ({department.subDepartments.length})
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {department.subDepartments.map((subDept) => (
              <Link key={subDept.departmentId} to={`/workforce/departments/${subDept.departmentId}` as any}>
                <Card className="p-4 card-hover">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-brand-primary mt-1" />
                    <div>
                      <div className="font-semibold text-white">{subDept.name}</div>
                      {subDept.description && (
                        <div className="text-sm text-brand-gray mt-1 line-clamp-2">
                          {subDept.description}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

// Apply RBAC protection
export default withPageProtection(DepartmentDetailContent, PAGES.WORKFORCE_DEPARTMENTS)
